import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FormInput from '../../components/forms/FormInput';
import FormPasswordInput from '../../components/forms/FormPasswordInput';
import { CheckCircle } from 'lucide-react';
import Button from '../../components/layout/ui/Button';

export default function Register() {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    password_confirmation: '',
    profile_type: 'customer'
  });
  const [loading, setLoading] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedLegal) {
      alert('You must accept the Terms & Conditions and Privacy Policy before creating an account.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/register`, {
        ...form,
        name: `${form.first_name} ${form.last_name}`
      });
      alert('Account created successfully! Please log in.');
      navigate('/login');
    } catch (err) {
      alert(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-8 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="/images/nanny.jpg"
          alt="Domestic worker platform"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/70 to-secondary/80" />
      </div>

      <div className="relative z-10 w-full max-w-5xl">
        <div className="grid md:grid-cols-2 bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-primary to-secondary p-10 md:p-16 text-white flex flex-col justify-center">
            <div className="mb-8">
              <img
                src="/mainlogowhite.png"
                alt="AuthWorka Logo"
                className="w-64 md:w-72 h-auto mb-6"
              />
              <p className="text-2xl md:text-3xl font-light opacity-95">
                Trusted. Structured. Professional.
              </p>
            </div>

            <div className="hidden md:block space-y-6 text-lg md:text-xl">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                  <CheckCircle />
                </div>
                <span>Build a trusted domestic worker or employer profile</span>
              </div>
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                  <CheckCircle />
                </div>
                <span>Access structured offers, contracts, and support tools</span>
              </div>
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                  <CheckCircle />
                </div>
                <span>Join a platform designed for workers, employers, agents, and admins</span>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-12 flex items-center justify-center bg-white">
            <div className="w-full max-w-md">
              <div className="text-center mb-10">
                <h2 className="text-4xl font-bold text-gray-900">Create Your Account</h2>
                <p className="text-gray-600 mt-3">
                  Join a trusted platform for domestic workers, employers, and workforce management
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <FormInput label="First Name" name="first_name" value={form.first_name} onChange={handleChange} required />
                  <FormInput label="Last Name" name="last_name" value={form.last_name} onChange={handleChange} required />
                </div>

                <FormInput label="Email Address" name="email" type="email" value={form.email} onChange={handleChange} required />
                <FormInput label="Phone Number" name="phone_number" value={form.phone_number} onChange={handleChange} placeholder="08012345678" required />

                <FormPasswordInput label="Password" name="password" value={form.password} onChange={handleChange} required />
                <FormPasswordInput label="Confirm Password" name="password_confirmation" value={form.password_confirmation} onChange={handleChange} required />

                <div className="flex justify-center gap-10 py-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="profile_type"
                      value="customer"
                      checked={form.profile_type === 'customer'}
                      onChange={handleChange}
                      className="w-6 h-6 text-primary"
                    />
                    <span className="font-semibold text-lg">I'm hiring</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="profile_type"
                      value="nanny"
                      checked={form.profile_type === 'nanny'}
                      onChange={handleChange}
                      className="w-6 h-6 text-primary"
                    />
                    <span className="font-semibold text-lg">I'm a worker</span>
                  </label>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedLegal}
                      onChange={(e) => setAcceptedLegal(e.target.checked)}
                      className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                      required
                    />
                    <span className="text-sm leading-6 text-gray-700">
                      I agree to the{' '}
                      <Link to="/terms" className="font-semibold text-primary hover:underline">
                        Terms & Conditions
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy-policy" className="font-semibold text-primary hover:underline">
                        Privacy Policy
                      </Link>.
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full text-lg"
                  loading={loading}
                  disabled={loading || !acceptedLegal}
                >
                  Create Account
                </Button>
              </form>

              <p className="text-center mt-10 text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-primary font-bold hover:underline text-lg">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}